import './AttachmentCreatePanel.css';
import * as React from 'react';
import { connect } from "react-redux";
import { Dictionary, EntityType } from "../../../entities/common";
import { ApplicationState } from "../../../store";
import { CreatePanel } from "../../common/SubentityPanel";
import { Icon, Link as FabricLink } from 'office-ui-fabric-react';
import { Attachment, AttachmentType, IAttachmentActions, IAttachmentAttrs } from "../../../entities/Subentities";
import { nameof, namesof } from "../../../store/services/metadataService";
import * as Metadata from '../../../entities/Metadata';
import { IValidator, Validator } from "../../../validation";
import { ProjectInfo } from '../../../store/ProjectsListStore';
import { ensureHttpsSchema } from '../../utils/common';
import TextInput from '../../common/inputs/TextInput';

const LinkDisplayFields = namesof<IAttachmentAttrs>(["Name", "Url", "Description", "Tags"]);
const FileDisplayFields = namesof<FileAttachmentAttrs>(["Name", "Description", "Tags", "DownloadFile"]);

export const validatorBuilder: Dictionary<(state: Attachment, field: Metadata.Field) => IValidator> = {
    [nameof<IAttachmentAttrs>("Url")]: (state, field) => {
        return Validator.new().required().url().build();
    }
}

const mapStateToProps = (state: ApplicationState) => {
    const fields = state.fields[EntityType.Attachment];
    return {
        fields: fields.allIds.map(_ => fields.byId[_]),
        storage: state.tenant.storage
    }
}

type Props = {
    fields: Metadata.Field[];
    actions?: IAttachmentActions;
    onDismiss: () => void;
}

export const AttachmentCreatePanel = connect(mapStateToProps)((props: Props) => {
    const { panelProps } = useAttachmentPanel(props);
    return <CreatePanel {...panelProps} onDismiss={props.onDismiss} />
})

export const useAttachmentPanel = ({ fields, actions }: { fields: Metadata.Field[], actions?: IAttachmentActions; }) => {
    const subentityInfo = React.useMemo(buildSubEntityInfo, []);

    const panelProps = {
        ...subentityInfo,
        displayFields: [...new Set([...LinkDisplayFields, ...FileDisplayFields])],
        customFieldValidatorBuilder: validatorBuilder,
        header: { showNameEditor: true },
        subentityTypeLabel: (entity: Attachment) => isLink(entity) ? 'Link' : 'Attachment',
        fields: (entity: Attachment) => isLink(entity)
            ? fields.filter(_ => LinkDisplayFields.includes(_.name)).map(makeRequired([nameof<IAttachmentAttrs>("Url")]))
            : fields.filter(_ => FileDisplayFields.includes(_.name)).concat([downloadFileField]),
        uiControlElementsCustomRender: {
            [downloadFileField.name]: (_: any, entity: Attachment) => <div className='field-container download-file-field'>
                <FabricLink href={actions?.getFileLink(entity)}><Icon iconName='Download' /> Download File</FabricLink>
            </div>,
            [nameof<IAttachmentAttrs>("Url")]: (inputProps: any, entity: Attachment, field: Metadata.Field, validator: IValidator) => isLink(entity)
                ? <TextInput
                    {...inputProps}
                    inputProps={{ readOnly: field.isReadonly }}
                    field={field}
                    validator={validator}
                    entityType={EntityType.Attachment}
                    maxLength={field.settings?.maxLength}
                    onChanged={_ => inputProps.onChanged?.(ensureHttpsSchema(_))}
                />
                : null
        },
        buildNewEntity: actions?.buildNew,
        onComplete: (entity: Attachment) => actions?.create?.(entity)
    }

    return { panelProps };
}

const makeRequired = (fields: string[]) => (field: Metadata.Field) => fields.includes(field.name)
    ? ({ ...field, settings: { ...field.settings, required: true } })
    : field;

export const buildSubEntityInfo = () => ({
    subentityType: EntityType.Attachment,
    subentityTypeLabel: "Attachment",
    subentityCollectionName: nameof<ProjectInfo>("attachments")
});

export type FileAttachmentAttrs = IAttachmentAttrs & { DownloadFile: string }

export const isLink = (entity: Attachment): entity is Attachment<IAttachmentAttrs> => entity.attributes.Type === AttachmentType.LinkedFile;

export const downloadFileField: Metadata.Field = {
    id: "39d7ea57-9e1a-4472-a941-c4f0db66805a",
    label: "",
    name: nameof<FileAttachmentAttrs>("DownloadFile"),
    type: Metadata.FieldType.Text,
    isNative: false,
    isCustom: false,
    isReadonly: false,
    isFake: true,
    isSystem: false,
    group: Metadata.FieldGroup.Details
}

export const fileSizeField: Metadata.Field = {
    id: "2940f98d-d299-4fa9-acd6-b3dfcb0c9661",
    label: "File Size",
    name: "FileSize",
    type: Metadata.FieldType.Text,
    isNative: false,
    isCustom: false,
    isReadonly: false,
    isFake: true,
    isSystem: false,
    group: Metadata.FieldGroup.Details,
    settings: { views: { list: { componentPath: "subentity/attachment/FileSize" } } },
}